import { useState, useCallback } from 'react';
import type { TourStep } from '@/components/ui/product-tour';

const TOUR_STORAGE_KEY = 'additions-tour-completed';

export function useAdditionsTour() {
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [hasCompletedTour, setHasCompletedTour] = useState(() => {
    return localStorage.getItem(TOUR_STORAGE_KEY) === 'true';
  });

  const startTour = useCallback(() => {
    setIsTourOpen(true);
  }, []);

  const closeTour = useCallback(() => {
    setIsTourOpen(false);
  }, []);

  const completeTour = useCallback(() => {
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    setHasCompletedTour(true);
    setIsTourOpen(false);
  }, []);

  const resetTour = useCallback(() => {
    localStorage.removeItem(TOUR_STORAGE_KEY);
    setHasCompletedTour(false);
  }, []);

  const tourSteps: TourStep[] = [
    {
      target: '[data-tour="additions-header"]',
      title: 'Welcome to Asset Additions',
      content: 'This is where you process asset additions in batches. You can add multiple assets to your register in a single transaction, making it efficient to onboard new assets.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="start-batch-button"]',
      title: 'Start New Addition Batch',
      content: 'Click here to begin a new addition batch. You\'ll be able to select assets and configure the addition details before processing.',
      placement: 'left',
    },
    {
      target: '[data-tour="view-summary-button"]',
      title: 'View Last Batch Summary',
      content: 'Quickly review the details and results of your most recently completed addition batch.',
      placement: 'left',
    },
    {
      target: '[data-tour="search-transactions"]',
      title: 'Search Transactions',
      content: 'Search through your addition transaction history by reference, date, or other details to quickly find specific batches.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="transaction-history"]',
      title: 'Transaction History',
      content: 'View all your addition batches - both in progress and completed. Click on any batch to view details or continue editing draft batches.',
      placement: 'top',
    },
    {
      target: '[data-tour="batch-status"]',
      title: 'Batch Status',
      content: 'Each batch shows its current status: Draft (in progress), Completed (processed), or Failed. You can resume draft batches or review completed ones.',
      placement: 'left',
    },
  ];

  return {
    isTourOpen,
    hasCompletedTour,
    tourSteps,
    startTour,
    closeTour,
    completeTour,
    resetTour,
  };
}
